import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  CalendarDays,
  MapPin,
  Users,
  Ticket,
  Share2,
  Heart,
  BadgeCheck,
} from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { locationLabel } from "@/lib/mappers";
import { PremiumEventView } from "@/components/premium/PremiumEventView";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
      <Skeleton className="mb-8 aspect-[21/9] w-full rounded-3xl" />
      <div className="grid gap-10 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-10 w-3/4 rounded-lg" />
          <Skeleton className="h-5 w-1/2 rounded" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const { data: event, loading, error, refetch } = useMockQuery(
    () => apiClient.getEvent(id),
    [id]
  );

  if (loading)
    return (
      <MainLayout>
        <DetailSkeleton />
      </MainLayout>
    );

  if (error)
    return (
      <MainLayout>
        <div className="mx-auto max-w-3xl px-5 py-24">
          <ErrorState
            title="Event not found"
            description="This event may have ended or the link is incorrect."
            error={error}
            onRetry={refetch}
          />
          <div className="mt-6 text-center">
            <Link to="/explore" className="text-sm font-semibold text-brand">
              ← Back to explore
            </Link>
          </div>
        </div>
      </MainLayout>
    );

  if (event.type === "premium") return <PremiumEventView event={event} />;

  const hasCapacity = Number.isFinite(event.capacity) && event.capacity > 0;
  const pct = hasCapacity ? Math.min(100, Math.round((event.attendees / event.capacity) * 100)) : 0;
  const spotsLeft = hasCapacity ? Math.max(0, event.capacity - event.attendees) : 0;

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="relative mb-8 aspect-[21/9] overflow-hidden rounded-3xl">
          <img
            src={event.cover}
            alt={event.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute left-5 top-5 flex gap-2">
            <Badge className="rounded-full bg-white/90 px-3 py-1 text-foreground hover:bg-white">
              {event.category}
            </Badge>
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {event.title}
            </h1>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-muted-foreground">
              <span className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-brand" />
                {fmtDate(event.startsAt)}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-brand" />
                {locationLabel(event.location)}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {event.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-secondary px-3 py-1 text-sm text-muted-foreground"
                >
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-8 border-t border-border pt-8">
              <h2 className="mb-3 font-heading text-xl font-bold tracking-tight text-foreground">
                About this event
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {event.description}
              </p>
            </div>

            <div className="mt-8 border-t border-border pt-8">
              <h2 className="mb-4 font-heading text-xl font-bold tracking-tight text-foreground">
                Location
              </h2>
              <Link
                to="/map"
                className="group relative flex h-56 items-end overflow-hidden rounded-2xl border border-border bg-brand/5 p-6"
              >
                <div
                  className="absolute inset-0 opacity-70"
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(0,102,255,0.06) 1px, transparent 1px), linear-gradient(rgba(0,102,255,0.06) 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                  }}
                />
                <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-white shadow-lg">
                  <MapPin className="h-5 w-5" />
                </span>
                <span className="relative text-sm font-medium text-foreground">
                  {event.location.name} · Open in map
                </span>
              </Link>
            </div>
          </div>

          {/* Sticky booking card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-5 rounded-3xl border border-border bg-card p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-heading text-2xl font-extrabold text-foreground">
                  {event.price}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" /> {event.attendees} going
                </span>
              </div>

              {hasCapacity && (
                <div>
                  <Progress value={pct} className="h-2" />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {pct}% full · {spotsLeft} spots left
                  </p>
                </div>
              )}

              <Button
                data-testid="event-register-btn"
                className="w-full rounded-full bg-brand py-6 text-base hover:bg-brand-hover"
              >
                <Ticket className="mr-2 h-4 w-4" /> Get tickets
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" data-testid="event-save-btn">
                  <Heart className="mr-2 h-4 w-4" /> Save
                </Button>
                <Button variant="outline" className="flex-1 rounded-full" data-testid="event-share-btn">
                  <Share2 className="mr-2 h-4 w-4" /> Share
                </Button>
              </div>

              <div className="border-t border-border pt-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Hosted by
                </p>
                <Link
                  to={`/profile/${event.host.username}`}
                  className="flex items-center gap-3"
                >
                  <Avatar className="h-11 w-11">
                    <AvatarImage src={event.host.avatar} alt={event.host.displayName} />
                    <AvatarFallback>{event.host.displayName[0]}</AvatarFallback>
                  </Avatar>
                  <span className="flex items-center gap-1 font-semibold text-foreground">
                    {event.host.displayName}
                    {event.host.verified && (
                      <BadgeCheck className="h-4 w-4 text-brand" />
                    )}
                  </span>
                </Link>
              </div>

              <p className="rounded-xl bg-secondary px-3 py-2 text-center text-xs text-muted-foreground">
                Ticketing is a placeholder in this preview.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
